/*
Add autocomplete features to DOM elements.

Data attributes:
- data-autocomplete-url: specify the API url to fetch data (required)
- data-autocomplete-type: specify the type of autocomplete (parse and display function, submit, etc.)
- data-autocomplete-keys: a JSON data that specify the keys for navigating the API response
- data-autocomplete-labels: a JSON data to map key into label (e.g. friendly_id to part id)

*/

const AUTOCOMPLETE_TYPES = {
    users: {
        parse: (data, keys) => {
            return data.results.map(item => ({
                ...item,
                string: keys.map(k => item[k]).filter(Boolean).join(' · '),
                submitValue: item['friendly_id']
            }));
        },
        display: (item, data, labels) => {
            item.innerHTML = `
            <div class="autoComplete_result">
                <span class="text-truncate text-dark">${data.match}</span>
            </div>
            `
        },
        submit: false,
    },
    default: {
        parse: (data, keys) => {
            // From fetched data returns one object per string in keys
            return data.results.flatMap(item => {
                return keys.flatMap(key => {
                    const value = item[key];
                    if (Array.isArray(value)) {
                        return value.map(v => ({...item, key: key, string: v, submitValue: v})); // array of multiple objects
                    } else if (typeof value === "string") {
                        return [{...item, key: key, string: value, submitValue: value}]; // array of single object
                    }
                    return [];
                });
            });
        },
        display: (item, data, labels) => {
            const label = labels[data.value.key] || data.value.key;
            item.innerHTML = `
            <div class="d-flex align-items-center justify-content-between autoComplete_result">
                <span class="text-truncate me-4 text-dark">${data.match}</span>
                <small class="opacity-50 text-capitalize">${label}</small>
            </div>
            `
        },
        submit: true,
    }
}

const AUTOCOMPLETE_SELECTOR = '[data-autocomplete-url]';

function initAutocomplete() {
    const autocompleteEl = document.querySelector(AUTOCOMPLETE_SELECTOR);
    if (!autocompleteEl) {
        return;
    }
    if (autocompleteEl.dataset.autocompleteInitialized) {
        return;
    }
    autocompleteEl.dataset.autocompleteInitialized = "true";

    autocompleteEl.addEventListener("keydown", e => {
        if (e.key === "Enter") {
            autocompleteEl.form.submit()
        }
    })
    const type = autocompleteEl.dataset.autocompleteType ? AUTOCOMPLETE_TYPES[autocompleteEl.dataset.autocompleteType] : AUTOCOMPLETE_TYPES.default;
    const keys = autocompleteEl.dataset.autocompleteKeys ? JSON.parse(autocompleteEl.dataset.autocompleteKeys) : ["name", 'friendly_id'];
    const labels = autocompleteEl.dataset.autocompleteLabels ? JSON.parse(autocompleteEl.dataset.autocompleteLabels) : {
        name: "Name",
        friendly_id: "Part ID."
    };
    import('@tarekraafat/autocomplete.js').then((module) => {
            const autoCompleteJS = new module.default({
                selector: AUTOCOMPLETE_SELECTOR,
                trigger: (query) => {
                    return query.replace(/ /g, "").length; // Returns "Boolean"
                },
                debounce: 300, // Milliseconds value
                data: {
                    src: async (query) => {
                        try {
                            // Fetch External Data Source
                            const sourceURL = autocompleteEl.dataset.autocompleteUrl;
                            const source = await fetch(`${sourceURL}?q=${query}`);
                            const data = await source.json();
                            return type.parse(data, keys);
                        } catch (error) {
                            console.error(error);
                            return [];
                        }
                    },
                    keys: ["string"],
                },
                resultItem: {
                    element: (item, data) => type.display(item, data, labels),
                    highlight: true,
                },
                events: {
                    input: {
                        focus() {
                            if (autoCompleteJS.input.value.length) autoCompleteJS.start();
                        },
                        selection(event) {
                            const feedback = event.detail;
                            autoCompleteJS.input.blur();
                            const selection = feedback.selection.value.submitValue;
                            autocompleteEl.value = selection;
                            autoCompleteJS.input.value = selection;
                            if (type.submit) {
                                autocompleteEl.form.submit();
                            }
                        },
                    },
                },
            })
        }
    )
}

document.addEventListener("DOMContentLoaded", initAutocomplete);

document.body.addEventListener("htmx:afterSwap", (e) => {
    if (e.target.querySelector(AUTOCOMPLETE_SELECTOR)) {
        initAutocomplete();
    }
});